import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { KPICard as KPICardType } from '@/types';

interface KPICardProps extends KPICardType {
  onClick?: () => void;
}

const statusGradients: Record<string, string> = {
  healthy: 'from-emerald-500/8 to-emerald-500/2',
  warning: 'from-amber-500/8 to-amber-500/2',
  critical: 'from-red-500/8 to-red-500/2',
};

const statusBorders: Record<string, string> = {
  healthy: 'border-emerald-500/20 hover:border-emerald-500/40',
  warning: 'border-amber-500/20 hover:border-amber-500/40',
  critical: 'border-red-500/20 hover:border-red-500/40',
};

const KPICard: React.FC<KPICardProps> = ({ label, value, trend, status, onClick }) => {
  const getTrendIcon = () => {
    switch (trend.direction) {
      case 'up':
        return <TrendingUp size={13} />;
      case 'down':
        return <TrendingDown size={13} />;
      default:
        return <Minus size={13} />;
    }
  };

  const trendColor = trend.direction === 'up' 
    ? 'text-emerald-400' 
    : trend.direction === 'down' 
      ? 'text-red-400' 
      : 'text-text-muted';

  return (
    <div 
      className={`
        relative overflow-hidden rounded-xl p-5 
        bg-gradient-to-br ${statusGradients[status] || statusGradients.healthy}
        border ${statusBorders[status] || statusBorders.healthy}
        bg-navy-card backdrop-blur-sm
        transition-all duration-300 ease-out
        hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5
        ${onClick ? 'cursor-pointer' : ''}
      `}
      onClick={onClick}
      role={onClick ? 'button' : 'article'}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      } : undefined}
    >
      {/* Subtle glow effect */}
      <div className="absolute -top-12 -right-12 w-24 h-24 rounded-full bg-gold-primary/[0.03] blur-2xl" />
      
      <p className="text-[11px] font-medium text-text-muted uppercase tracking-wider mb-1.5">
        {label}
      </p>
      
      <p className="text-2xl font-bold text-text-heading mb-2 tracking-tight">
        {value}
      </p>
      
      <div className={`flex items-center space-x-1 text-xs ${trendColor}`}>
        {getTrendIcon()}
        <span className="font-medium">
          {trend.direction === 'up' ? '+' : trend.direction === 'down' ? '' : ''}
          {trend.direction === 'down' ? '-' : ''}{Math.abs(trend.percentage)}%
        </span>
        <span className="text-text-muted font-normal">({trend.period})</span>
      </div>

      {/* Status dot */}
      <div className="absolute top-4 right-4">
        <div className={`w-2 h-2 rounded-full ${
          status === 'healthy' ? 'bg-emerald-400' : 
          status === 'warning' ? 'bg-amber-400' : 'bg-red-400'
        }`}>
          <div className={`w-2 h-2 rounded-full animate-ping ${
            status === 'healthy' ? 'bg-emerald-400' : 
            status === 'warning' ? 'bg-amber-400' : 'bg-red-400'
          }`} style={{ animationDuration: '3s' }} />
        </div>
      </div>
    </div>
  );
};

export default KPICard;
